import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import { ListItem } from '@ipfabric/ui-components/List/ListItem/ListItem';
import type { TableColumnConfig } from '@ipfabric/utils';
import { prop } from '@ipfabric/utils/fp/prop';
import { TableSettingsColumnStickyToggle } from 'components/Table/components/TableSettings/TableSettingsColumnStickyToggle';
import { TableSettingsColumnVisibilityToggle } from 'components/Table/components/TableSettings/TableSettingsColumnVisibilityToggle';
import { useTableContextSelectors } from 'components/Table/model/TableModel';
import { saveTableSettings } from 'components/Table/model/commands/saveTableSettings';
import { sortTableSettingsColumns } from 'components/Table/model/commands/sortTableSettingsColumns';
import { selectVisibleColumns } from 'components/Table/model/selectors/selectVisibleColumns';
import { type FC, memo, useRef } from 'react';
import { type DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { canSort } from 'utils/dnd/canSort';
import { useEmptyPreview } from 'utils/dnd/useEmptyPreview';

interface Props {
  columnIndex: number;
  columnIsDragged: boolean;
  column: TableColumnConfig;
  onColumnIsDraggedChange: (columnKey: TableColumnConfig['key'] | null) => void;
}

export interface TableSettingsColumnDragItem {
  column: TableColumnConfig;
  index: number;
  type: string;
}

export const TABLE_SETTINGS_COLUMN_DRAG_TYPE = 'tableColumnSettings';

const BareTableSettingsColumn: FC<Props> = (props) => {
  const ctxProps = useTableContextSelectors({
    columnVisibility: prop('settings', 'columnVisibility'),
    visibleColumns: selectVisibleColumns,
    dispatch: prop('dispatch'),
  });
  const itemRef = useRef<HTMLDivElement>(null);
  const dragElementRef = useRef<HTMLDivElement>(null);

  const [, drag, preview] = useDrag({
    begin: () => {
      setTimeout(() => {
        props.onColumnIsDraggedChange(props.column.key);
      }, 0);
    },
    end: () => {
      props.onColumnIsDraggedChange(null);
    },
    item: {
      column: props.column,
      index: props.columnIndex,
      type: TABLE_SETTINGS_COLUMN_DRAG_TYPE,
    },
  });

  const [, drop] = useDrop({
    accept: TABLE_SETTINGS_COLUMN_DRAG_TYPE,
    hover(item: TableSettingsColumnDragItem, monitor: DropTargetMonitor) {
      if (!itemRef.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = props.columnIndex;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      if (!canSort(itemRef.current, monitor, dragIndex, hoverIndex)) {
        return;
      }

      ctxProps.dispatch(sortTableSettingsColumns, { dragIndex, hoverIndex });

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.

      // Example: https://github.com/react-dnd/react-dnd/blob/09838c9c203554bfe23c97f7af82b120f7f28be8/packages/documentation/examples-hooks/src/04-sortable/simple/Card.tsx
      item.index = hoverIndex;
    },
    drop() {
      // Wait for updated table settings
      setTimeout(() => {
        ctxProps.dispatch(saveTableSettings);
      }, 0);
    },
  });

  drop(itemRef);
  drag(dragElementRef);
  useEmptyPreview(preview);

  return (
    <ListItem
      className='select-none'
      forwardRef={itemRef}
      label={props.column.title}
      startAdornment={
        <Icon
          iconStyle='solid'
          className='cursor-move'
          glyph='fa-grip-dots-vertical'
          wrapperRef={dragElementRef}
        />
      }
      endAdornment={
        <>
          <TableSettingsColumnStickyToggle
            className='mr-2'
            column={props.column}
          />
          <TableSettingsColumnVisibilityToggle column={props.column} />
        </>
      }
    />
  );
};

export const TableSettingsColumn = memo(BareTableSettingsColumn);
